import { createSensor, updateSensor, useSensorForm } from '@gozoki/api'
import { Button, IconButton, TextField, Typography } from '@mui/material'
import { skipToken, useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import z from 'zod'
import { useToast } from '~/components/communication/Toast'
import { api } from '~/lib/tuyau'
import { getApiFieldErrors } from '~/lib/utils'
import { withRoutePermissions } from '~/utils/rbac'
import { AuthorizedButton } from '../../../components/communication/AuthorizedButton'
import { fridgesQueryOptions } from '../../../components/routes/sensors/queries'
import { SENSOR_ADD, SENSOR_EDIT } from '../../../components/routes/sensors/tabs'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import SelectRhf from '../../../components/inputs/SelectRhf'
import TextFieldRhf from '../../../components/inputs/TextFieldRhf'
import Page from '../../../components/Page'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/sensors/new')({
  // Écran partagé création / édition (search param `sensorId`) : l'un ou l'autre droit y donne
  // accès, le bouton de soumission exigeant ensuite celui du mode courant.
  staticData: {
    permission: { any: [SENSOR_ADD, SENSOR_EDIT] },
  },
  component: AddSensorPage,
  validateSearch: z.object({
    sensorId: z.number().optional(),
  }),
})
withRoutePermissions(Route)

// Champs dont seul le backend peut invalider la valeur (unicité) : le reste est déjà bloqué par Zod.
const SENSOR_FORM_FIELDS = ['label', 'reference'] as const

function AddSensorPage() {
  const navigate = Route.useNavigate()
  const { sensorId } = Route.useSearch()

  const updateMode = !!sensorId

  const [Toast, showToast] = useToast()

  const { data, isLoading } = useQuery(
    api.sensors.show.queryOptions(sensorId ? { params: { id: sensorId } } : skipToken),
  )

  const { control, trigger, getValues, setValue, setError } = useSensorForm()

  const sensorType = useWatch({ control, name: 'type', defaultValue: 'FRIDGE' })

  const { data: fridges = [] } = useQuery(fridgesQueryOptions())
  const { data: fridgeStores = [] } = useQuery(api.stores.fridgeStores.queryOptions({}))

  useEffect(() => {
    if (data) {
      setValue('reference', data.reference || '')
      setValue('label', data.label)
      setValue('description', data.description || '')
      setValue('model', data.model || '')
      setValue('temperatureOffset', data.temperatureOffset ?? 0)
      if (data.type === 'ROOM') {
        setValue('tempMinOverride', data.tempMinOverride ?? undefined)
        setValue('tempMaxOverride', data.tempMaxOverride ?? undefined)
        setValue('breachToleranceMinutesOverride', data.breachToleranceMinutesOverride ?? undefined)
        setValue(
          'inactivityThresholdMinutesOverride',
          data.inactivityThresholdMinutesOverride ?? undefined,
        )
      }
      setValue('type', data.type)
      if (data.type === 'FRIDGE' && data.fridge) {
        setValue('fridgeId', data.fridge.id)
        setValue('storeId', undefined)
      }
      if (data.type === 'ROOM' && data.store) {
        setValue('storeId', data.store.id)
        setValue('fridgeId', undefined)
      }
    }
  }, [setValue, data])

  const navigateBack = () => {
    navigate({ search: { tab: 'sensors' }, to: '/sensors' })
  }

  // Les erreurs de validation backend (dont les conflits d'unicité) sont rattachées à leur champ ;
  // le reste part en toast générique — on ne remonte jamais le corps d'erreur brut de l'API.
  const handleMutationError = (error: unknown, fallbackMessage: string) => {
    const fieldErrors = getApiFieldErrors(error, SENSOR_FORM_FIELDS)
    if (fieldErrors.length > 0) {
      for (const { field, message } of fieldErrors) {
        setError(field, { type: 'manual', message })
      }
      return
    }

    showToast({ message: fallbackMessage, severity: 'error' })
  }

  const { mutate: upload, isPending: isUploading } = useMutation({
    mutationFn: createSensor,
    meta: { invalidates: [api.sensors.index.queryKey()] },
    onSuccess: navigateBack,
    onError: (error: unknown) =>
      handleMutationError(error, 'Une erreur est survenue lors de la création du capteur'),
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: updateSensor,
    meta: { invalidates: [api.sensors.index.queryKey(), api.sensors.show.queryKey()] },
    onSuccess: navigateBack,
    onError: (error: unknown) =>
      handleMutationError(error, 'Une erreur est survenue lors de la modification du capteur'),
  })

  const uploadSensor = async () => {
    const valid = await trigger()

    if (valid) {
      upload(getValues())
    }
  }

  const handleUpdateSensor = async () => {
    const valid = await trigger()

    if (valid && sensorId) {
      update({
        id: sensorId,
        data: getValues(),
      })
    }
  }

  let locationField: React.ReactNode = <></>
  if (sensorType === 'FRIDGE') {
    locationField = (
      <>
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Réfrigérateur
        </Typography>
        <SelectRhf
          control={control}
          name="fridgeId"
          options={fridges.map((fridge) => ({
            value: fridge.id,
            label: `${fridge.label} (${fridge.type})${
              fridge.store ? ` - ${fridge.store.label}` : ''
            }`,
          }))}
        />
      </>
    )
  } else {
    locationField = (
      <>
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Magasin
        </Typography>
        <SelectRhf
          control={control}
          name="storeId"
          options={fridgeStores.map((store) => ({
            value: store.id,
            label: store.label,
          }))}
        />
      </>
    )
  }

  return (
    <Page sx={{ maxWidth: '50vw', margin: 'auto' }}>
      <Toast />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={navigateBack}>
          <ArrowLeft
            style={{
              color: ThemeColors.BLACK,
              height: '24px',
              width: '24px',
            }}
          />
        </IconButton>
        <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
          {updateMode ? 'Modifier un capteur' : 'Ajouter un capteur'}
        </Typography>

        <Button
          variant="outlined"
          sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
          onClick={navigateBack}
        >
          Annuler
        </Button>
        <AuthorizedButton
          permission={updateMode ? SENSOR_EDIT : SENSOR_ADD}
          variant="contained"
          disabled={isUpdating || isUploading}
          onClick={updateMode ? handleUpdateSensor : uploadSensor}
        >
          {updateMode ? 'Modifier' : 'Ajouter'}
        </AuthorizedButton>
      </div>
      <LoadingPaper
        sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
        loading={isLoading && updateMode}
      >
        <Typography mb="8px" style={fonts.inputHeader}>
          Type de capteur
        </Typography>
        <SelectRhf
          control={control}
          name="type"
          disabled={updateMode}
          options={[
            { value: 'FRIDGE', label: 'Capteur réfrigérateur' },
            { value: 'ROOM', label: 'Capteur température ambiante' },
          ]}
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Référence (devEUI)
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Référence (devEUI) (optionnel)"
          color="info"
          control={control}
          trigger={trigger}
          name="reference"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Libellé *
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Libellé du capteur"
          color="info"
          control={control}
          trigger={trigger}
          name="label"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Description
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Description (optionnel)"
          color="info"
          control={control}
          trigger={trigger}
          name="description"
          fullWidth
          multiline
          rows={3}
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Modèle
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Modèle du capteur (optionnel)"
          color="info"
          control={control}
          trigger={trigger}
          name="model"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Offset de sonde (°C)
        </Typography>
        <Controller
          control={control}
          name="temperatureOffset"
          render={({ field, fieldState }) => (
            <TextField
              variant="outlined"
              type="number"
              placeholder="0"
              color="info"
              fullWidth
              value={field.value ?? ''}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '') {
                  field.onChange(undefined)
                  return
                }
                // Frappe partielle (ex. "-" seul sur Firefox) → Number() = NaN :
                // on conserve la dernière valeur valide plutôt que d'injecter NaN.
                const parsed = Number(raw)
                field.onChange(Number.isNaN(parsed) ? field.value : parsed)
              }}
              onBlur={() => field.onBlur()}
              error={fieldState.error !== undefined}
              helperText={
                fieldState.error?.message ??
                'Correction appliquée à la mesure brute (ex. -1.5). Température corrigée = mesure + offset.'
              }
            />
          )}
        />
        {locationField}
        {sensorType === 'ROOM' && (
          <>
            <Typography mb="4px" mt="32px" style={fonts.inputHeader}>
              Seuils personnalisés (capteur ambiant)
            </Typography>
            <Typography mb="8px" variant="body2" color="text.secondary">
              Laisser vide pour utiliser le défaut de la catégorie.
            </Typography>
            <Typography mb="8px" mt="16px" style={fonts.inputHeader}>
              Seuil min personnalisé (°C)
            </Typography>
            <Controller
              control={control}
              name="tempMinOverride"
              render={({ field, fieldState }) => (
                <TextField
                  variant="outlined"
                  type="number"
                  placeholder="vide = défaut catégorie"
                  color="info"
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value
                    if (raw === '') {
                      field.onChange(null)
                      return
                    }
                    const parsed = Number(raw)
                    field.onChange(Number.isNaN(parsed) ? field.value : parsed)
                  }}
                  onBlur={() => field.onBlur()}
                  error={fieldState.error !== undefined}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Seuil max personnalisé (°C)
            </Typography>
            <Controller
              control={control}
              name="tempMaxOverride"
              render={({ field, fieldState }) => (
                <TextField
                  variant="outlined"
                  type="number"
                  placeholder="vide = défaut catégorie"
                  color="info"
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value
                    if (raw === '') {
                      field.onChange(null)
                      return
                    }
                    const parsed = Number(raw)
                    field.onChange(Number.isNaN(parsed) ? field.value : parsed)
                  }}
                  onBlur={() => field.onBlur()}
                  error={fieldState.error !== undefined}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Tolérance de dépassement personnalisée (min)
            </Typography>
            <Controller
              control={control}
              name="breachToleranceMinutesOverride"
              render={({ field, fieldState }) => (
                <TextField
                  variant="outlined"
                  type="number"
                  placeholder="vide = défaut catégorie"
                  color="info"
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value
                    if (raw === '') {
                      field.onChange(null)
                      return
                    }
                    const parsed = Number(raw)
                    field.onChange(Number.isNaN(parsed) ? field.value : parsed)
                  }}
                  onBlur={() => field.onBlur()}
                  error={fieldState.error !== undefined}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Seuil d'inactivité personnalisé (min)
            </Typography>
            <Controller
              control={control}
              name="inactivityThresholdMinutesOverride"
              render={({ field, fieldState }) => (
                <TextField
                  variant="outlined"
                  type="number"
                  placeholder="vide = défaut catégorie"
                  color="info"
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value
                    if (raw === '') {
                      field.onChange(null)
                      return
                    }
                    const parsed = Number(raw)
                    field.onChange(Number.isNaN(parsed) ? field.value : parsed)
                  }}
                  onBlur={() => field.onBlur()}
                  error={fieldState.error !== undefined}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </>
        )}
      </LoadingPaper>
    </Page>
  )
}
